import { useMemo } from 'react';
import { useRouter } from '@edreamsodigeo/frontend-core';

import { useRegexProvider } from '@/clientState/providers/FlightSummaryMetaProvider/FlightSummaryMetaProvider';
import { useMetaFlowProvider } from '@/clientState/providers/MetaFlowProvider';
import type { QualityTagType } from '@/components/FlightSummary/AirlineQualityTag/AirlineQualityTagCard/config/types';
import { FeeSelected } from '@/generated/graphql';

type FeeTypeId =
  | 'MEMBER_PRICE_POLICY_DISCOUNTED'
  | 'MEMBER_PRICE_POLICY_UNDISCOUNTED'
  | 'CHEAPEST'
  | number;

interface ItineraryParams {
  searchId: number;
  carbonFootprint: number;
  fareItineraryKey: string;
  collectionMethodId: number;
  feeTypeId: FeeTypeId;
  segmentKeys: Array<number>;
  feeSelected: FeeSelected;
  highlightOffer: boolean;
  qualityTagType: QualityTagType;
}

const useItineraryFromUrl = (): ItineraryParams => {
  const { pathname } = useRouter();
  const isMeta = useMetaFlowProvider();
  const regex = useRegexProvider();

  const itineraryParamsEncoded = useMemo(() => {
    const itineraryUrl = new RegExp(
      regex || /.*checkout_flight_summary_results\/(.*)\//
    );
    const [, itineraryParamsEncoded] = pathname.match(itineraryUrl) || [];
    return itineraryParamsEncoded;
  }, [pathname, regex]);

  let itineraryParams;

  try {
    itineraryParams = JSON.parse(window.atob(itineraryParamsEncoded));
  } catch {
    itineraryParams = {};
  }

  if (isMeta && itineraryParams?.segmentKeys) {
    itineraryParams['segmentKeys'] = itineraryParams['segmentKeys'].map(
      (segmentKey) => parseInt(segmentKey.charAt(0), 10)
    );
  }

  const feeTypeId = itineraryParams.feeTypeId;
  const feeTypeNumber = parseInt(feeTypeId, 10);
  itineraryParams['feeSelected'] =
    feeTypeId === 'MEMBER_PRICE_POLICY_DISCOUNTED'
      ? FeeSelected.PrimePrice
      : FeeSelected.FullPrice;
  itineraryParams['collectionMethodId'] = !isNaN(feeTypeNumber)
    ? feeTypeNumber
    : -1;
  return useMemo(() => itineraryParams, [itineraryParams]);
};

export default useItineraryFromUrl;
